import apiBaseUrl from '../../utils/config.js';

class PatientFileService {
  constructor() {
    this.baseUrl = `${apiBaseUrl}/patient-files`;
  }

  getToken() {
    return localStorage.getItem('jwt');
  }

  getAuthHeaders(extra = {}) {
    const token = this.getToken();

    return {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...extra,
    };
  }

  async getErrorMessage(response, fallbackMessage) {
    try {
      const contentType = response.headers.get('content-type') || '';

      if (contentType.includes('application/json')) {
        const data = await response.json();
        return data.message || fallbackMessage;
      }

      const text = await response.text();
      return text || fallbackMessage;
    } catch {
      return fallbackMessage;
    }
  }

  // =========================
  // Upload
  // PATIENT or staff
  // =========================
  async uploadFile({ patientId, file, filename }) {
    const formData = new FormData();

    formData.append('patientId', patientId);
    formData.append('file', file);

    if (filename) {
      formData.append('filename', filename);
    }

    const response = await fetch(`${this.baseUrl}/upload`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: formData, // Do not set Content-Type manually for FormData
    });

    if (!response.ok) {
      const errorMessage = await this.getErrorMessage(
        response,
        'Failed to upload file'
      );

      throw new Error(errorMessage);
    }

    return await response.json();
  }

  // =========================
  // List files
  // PATIENT only
  // =========================
  async listMine(patientId) {
  const response = await fetch(`${this.baseUrl}/patient-self/${patientId}`, {
    method: 'GET',
    headers: this.getAuthHeaders(),
  });

    if (!response.ok) {
      const errorMessage = await this.getErrorMessage(
        response,
        'Failed to load my files'
      );

      throw new Error(errorMessage);
    }

    return await response.json();
  }

  // =========================
  // List files
  // Staff only
  // =========================
  async listByPatientId(patientId) {
    const response = await fetch(`${this.baseUrl}/patient/${patientId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorMessage = await this.getErrorMessage(
        response,
        'Failed to load patient files'
      );

      throw new Error(errorMessage);
    }

    return await response.json();
  }

  // =========================
  // Usage
  // PATIENT self only
  // =========================
 async getMyUsage(patientId) {
  const response = await fetch(`${this.baseUrl}/usage/patient-self/${patientId}`, {
    method: 'GET',
    headers: this.getAuthHeaders(),
  });

    if (!response.ok) {
      const errorMessage = await this.getErrorMessage(
        response,
        'Failed to load storage usage'
      );

      throw new Error(errorMessage);
    }

    return await response.json();
  }

  // =========================
  // Usage
  // Staff by patientId
  // =========================
  async getUsageByPatientId(patientId) {
    const response = await fetch(`${this.baseUrl}/usage/patient/${patientId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorMessage = await this.getErrorMessage(
        response,
        'Failed to load patient storage usage'
      );

      throw new Error(errorMessage);
    }

    return await response.json();
  }

  // =========================
  // Download URL
  // =========================
  getDownloadUrl(publicId, { attachment = false } = {}) {
    const qs = new URLSearchParams();
    qs.set('attachment', attachment ? 'true' : 'false');

    return `${this.baseUrl}/download/${publicId}?${qs.toString()}`;
  }

  // =========================
  // Download blob
  // Used for preview and manual download
  // =========================
  async downloadBlob(publicId, { attachment = false } = {}) {
    const url = this.getDownloadUrl(publicId, { attachment });

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorMessage = await this.getErrorMessage(
        response,
        'Failed to download file'
      );

      throw new Error(errorMessage);
    }

    return await response.blob();
  }

  // =========================
  // Force browser download
  // =========================
  async forceDownload(publicId, filename = 'download', { attachment = true } = {}) {
    const blob = await this.downloadBlob(publicId, { attachment });

    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;

    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(blobUrl);
  }

  // =========================
  // Rename
  // =========================
  async rename(publicId, newName) {
    const url = `${this.baseUrl}/rename/${publicId}?newName=${encodeURIComponent(
      newName
    )}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorMessage = await this.getErrorMessage(
        response,
        'Failed to rename file'
      );

      throw new Error(errorMessage);
    }

    return await response.json();
  }

  // =========================
  // Delete
  // =========================
  async delete(publicId) {
    const response = await fetch(`${this.baseUrl}/${publicId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorMessage = await this.getErrorMessage(
        response,
        'Failed to delete file'
      );

      throw new Error(errorMessage);
    }

    const contentType = response.headers.get('content-type') || '';

    if (contentType.includes('application/json')) {
      return await response.json();
    }

    return await response.text();
  }
}

export { PatientFileService };
export default new PatientFileService();